import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PhoneIncoming, PhoneOff, Phone, Building2, History, Activity, Volume2, VolumeX, UserRound } from "lucide-react";
import api, { API, getToken } from "@/lib/crmApi";
import { cn } from "@/lib/utils";
import { useTwilio } from "@/context/TwilioVoice";
import { CALLER_META, RESULT_META, fmtDate } from "@/crm/components/telephony/shared";
import UnknownCallerModal from "@/crm/components/telephony/UnknownCallerModal";
import { StatusBadge } from "@/crm/ui";

function createRinger() {
  const state = { ctx: null, gain: null, oscs: [], timers: [], muted: false, running: false };
  const start = () => {
    if (state.running) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      state.ctx = state.ctx || new Ctx();
      if (state.ctx.state === "suspended") state.ctx.resume().catch(() => {});
      const gain = state.ctx.createGain();
      gain.gain.value = 0;
      gain.connect(state.ctx.destination);
      state.gain = gain;
      state.oscs = [440, 480].map((f) => {
        const o = state.ctx.createOscillator();
        o.type = "sine"; o.frequency.value = f; o.connect(gain); o.start();
        return o;
      });
      state.running = true;
      const cycle = () => {
        if (!state.running) return;
        gain.gain.setTargetAtTime(state.muted ? 0 : 0.07, state.ctx.currentTime, 0.02);
        state.timers.push(setTimeout(() => {
          if (state.running) gain.gain.setTargetAtTime(0, state.ctx.currentTime, 0.02);
        }, 1500));
        state.timers.push(setTimeout(cycle, 4000));
      };
      cycle();
    } catch { /* audio bloqué par le navigateur */ }
  };
  const stop = () => {
    state.running = false;
    state.timers.forEach(clearTimeout); state.timers = [];
    try {
      state.gain?.gain.setTargetAtTime(0, state.ctx.currentTime, 0.01);
      state.oscs.forEach((o) => o.stop(state.ctx.currentTime + 0.05));
    } catch { /* noop */ }
    state.oscs = []; state.gain = null;
  };
  const setMuted = (m) => {
    state.muted = m;
    if (state.running && state.gain && m) {
      try { state.gain.gain.setTargetAtTime(0, state.ctx.currentTime, 0.01); } catch { /* noop */ }
    }
  };
  return { start, stop, setMuted };
}

export default function IncomingCallPopup() {
  const tw = useTwilio();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [muted, setMuted] = useState(false);
  // Appelant non reconnu : la proposition de fiche survit à la fermeture de la
  // popup, sinon elle disparaîtrait au moment même où l'appel est pris.
  const [inconnu, setInconnu] = useState(null);
  const dismissed = useRef(new Set());
  const ringer = useRef(null);
  if (!ringer.current) ringer.current = createRinger();

  // Poussé en temps réel par le backend (remplace l'ancien polling 3s de /telephony/ring).
  // Le token est passé en sous-protocole WebSocket (pas en query param) pour ne pas finir dans les logs d'accès.
  useEffect(() => {
    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let stopped = false;

    const handleMessage = (evt) => {
      try {
        const d = JSON.parse(evt.data);
        if (d.ring && !dismissed.current.has(d.ring.id)) setData(d);
        else if (!d.ring) setData(null);
      } catch { /* message inattendu, ignoré */ }
    };

    const connect = () => {
      const token = getToken();
      if (!token) { reconnectTimer = setTimeout(connect, 2000); return; }
      ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/ring`, [token]);
      ws.onmessage = handleMessage;
      ws.onopen = () => {
        // Garde la connexion vivante à travers les proxys qui coupent les WS inactifs.
        pingTimer = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000);
      };
      ws.onclose = () => {
        clearInterval(pingTimer);
        if (!stopped) reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => ws.close();
    };
    connect();

    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      ws?.close();
      ringer.current.stop();
    };
  }, []);

  const ring = data?.ring;
  const ringSeconds = (() => {
    if (!ring) return 20;
    if (ring.countdown_seconds) return ring.countdown_seconds;
    const step = (data.call?.steps || []).find((s) => s.order === ring.step_order);
    return step?.ring_seconds || 20;
  })();

  // Sonnerie + compte à rebours par sonnerie (ring.id)
  useEffect(() => {
    if (!ring) { ringer.current.stop(); setRemaining(null); return; }
    // Chaque appel sonne : couper la sonnerie vaut pour l'appel qu'on a sous
    // les yeux, jamais pour ceux qui suivront. Un silence qu'on aurait oublié
    // d'annuler ferait manquer les appels suivants sans rien signaler.
    setMuted(false);
    ringer.current.setMuted(false);
    tw?.muteRingtone?.(false);
    ringer.current.start();
    const startedAt = new Date(ring.created_at).getTime();
    const tick = () => {
      const left = Math.max(0, ringSeconds - Math.round((Date.now() - startedAt) / 1000));
      setRemaining(left);
      if (left <= 0) {
        ringer.current.stop();
        dismissed.current.add(ring.id);
        setData(null);
      }
    };
    tick();
    const t = setInterval(tick, 500);
    return () => {
      clearInterval(t);
      ringer.current.stop();
      // La sonnerie du SDK est rendue dès que cet appel est terminé : le
      // réglage ne doit pas survivre à l'appel pour lequel on l'a pris.
      tw?.muteRingtone?.(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ring?.id]);

  const modaleInconnu = inconnu ? (
    <UnknownCallerModal
      phone={inconnu.phone}
      onClose={() => setInconnu(null)}
      onCreated={(c) => {
        setInconnu(null);
        navigate(c.company_id ? `/entreprises/${c.company_id}` : `/contacts/${c.id}`);
      }}
    />
  ) : null;

  if (!ring) return modaleInconnu;
  const { call, company, contact, history_calls, activities } = data;
  const meta = CALLER_META[call.caller_type];
  const pct = Math.max(0, Math.min(100, ((remaining ?? ringSeconds) / ringSeconds) * 100));

  const close = () => { ringer.current.stop(); dismissed.current.add(ring.id); setData(null); };

  const answer = async () => {
    if (tw?.incomingInfo) tw.acceptIncoming();
    try {
      await api.post(`/telephony/ring/${ring.id}/answer`);
      toast.success(`Appel du ${call.phone_number} pris — journalisé`);
    } catch (e) { toast.error(e.response?.data?.detail || "Appel déjà terminé"); }
    const numero = call.phone_number;
    const entreprise = company?.id;
    const contactId = contact?.id;
    close();
    // Screen-pop : la fiche connue s'ouvre d'elle-même ; un numéro inconnu
    // ouvre une proposition de création, au lieu de fabriquer en silence une
    // fiche « Nouveau contact » vide qu'il faudra compléter et rattacher plus tard.
    if (entreprise) {
      navigate(`/entreprises/${entreprise}`);
    } else if (contactId) {
      navigate(`/contacts/${contactId}`);
    } else {
      setInconnu({ phone: numero });
    }
  };

  const decline = async () => {
    if (tw?.incomingInfo) tw.rejectIncoming();
    await api.post(`/telephony/ring/${ring.id}/decline`).catch(() => {});
    close();
  };

  const toggleMute = () => {
    const m = !muted;
    setMuted(m);
    ringer.current.setMuted(m);          // la sonnerie de cette fenêtre
    tw?.muteRingtone?.(m);               // celle que joue le SDK Twilio
  };

  return (
    <>
    {modaleInconnu}
    <div className="fixed bottom-5 right-3 left-3 sm:left-auto sm:right-5 z-[60] sm:w-96 animate-slide-up" data-testid="incoming-call-popup">
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
        <div className="p-4 flex items-center gap-3 bg-gradient-to-r from-indigo-700 to-indigo-600">
          <div className="relative shrink-0">
            <div className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center">
              <PhoneIncoming size={20} />
            </div>
            <span className="absolute inset-0 rounded-full bg-white/25 animate-ping" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-indigo-200 uppercase tracking-wide">Appel entrant · {ring.step_label}</div>
            <div className="font-mono font-bold text-lg" data-testid="popup-phone-number">{call.phone_number}</div>
          </div>
          <span className={cn("shrink-0 px-2 py-0.5 rounded-md text-xs font-semibold", meta?.cls)}>{meta?.label}</span>
        </div>

        {/* Compte à rebours */}
        <div className="px-4 pt-3" data-testid="popup-countdown">
          <div className="flex items-center justify-between gap-2 text-xs mb-1">
            {/* Le bouton remplace l'ancien libellé « Sonnerie en cours… » : une
                icône seule, sans mot, dans le bandeau, ne se voyait pas — et
                une sonnerie qu'on ne sait pas couper, on la subit. */}
            <button data-testid="popup-mute-btn" onClick={toggleMute} aria-pressed={muted}
              title={muted ? "Rétablir la sonnerie de cet appel" : "Couper la sonnerie de cet appel"}
              className={cn("-ml-1.5 flex items-center gap-1.5 rounded-lg px-1.5 py-1 transition-colors",
                muted ? "bg-white/10 text-slate-300 hover:text-white"
                      : "text-slate-400 hover:bg-white/10 hover:text-white")}>
              {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
              {muted ? "Sonnerie coupée" : "Couper la sonnerie"}
            </button>
            <span className={cn("font-mono font-bold", (remaining ?? ringSeconds) <= 5 ? "text-rose-400" : "text-emerald-400")} data-testid="popup-countdown-seconds">
              {remaining ?? ringSeconds}s
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className={cn("h-full rounded-full transition-[width] duration-500 ease-linear",
              (remaining ?? ringSeconds) <= 5 ? "bg-rose-500" : "bg-emerald-500")}
              style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
          {company ? (
            <div className="text-sm space-y-1">
              <div className="flex items-center gap-2">
                <Building2 size={14} className="text-indigo-400 shrink-0" />
                <b data-testid="popup-company-name">{company.nom}</b>
                <StatusBadge status={company.status} />
              </div>
              <div className="text-xs text-slate-400 pl-6">
                {[company.ville, company.libelle_naf].filter(Boolean).join(" · ") || "—"}
                {company.commercial_name && <> · Responsable : <span className="text-slate-300">{company.commercial_name}</span></>}
              </div>
              {contact && <div className="text-xs text-slate-400 pl-6">Contact : {contact.prenom} {contact.nom}{contact.fonction ? ` (${contact.fonction})` : ""}</div>}
              {/* Consultation pendant la sonnerie : le popup vit dans la mise
                  en page générale, naviguer ne l'interrompt pas — l'appel
                  continue de sonner pendant qu'on lit la fiche. */}
              <div className="flex flex-wrap gap-2 pl-6 pt-1">
                <button data-testid="popup-open-company" onClick={() => navigate(`/entreprises/${company.id}`)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs">
                  <Building2 size={12} /> Ouvrir la fiche entreprise
                </button>
                {contact?.id && (
                  <button data-testid="popup-open-contact" onClick={() => navigate(`/contacts/${contact.id}`)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs">
                    <UserRound size={12} /> Fiche contact
                  </button>
                )}
              </div>
            </div>
          ) : contact ? (
            // Contact sans entreprise : sa fiche existe, l'annoncer comme
            // inconnu poussait à en créer une seconde pour le même numéro.
            <div className="text-sm space-y-1.5" data-testid="popup-contact-libre">
              <div className="flex items-center gap-2">
                <UserRound size={15} className="text-indigo-300 shrink-0" />
                <span className="font-medium">{[contact.prenom, contact.nom].filter(Boolean).join(" ")}</span>
                {contact.fonction && <span className="text-slate-400 text-xs">{contact.fonction}</span>}
                <span className="ml-auto text-[11px] text-slate-400 shrink-0">contact libre</span>
              </div>
              <button data-testid="popup-open-contact" onClick={() => navigate(`/contacts/${contact.id}`)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs ml-6">
                <UserRound size={12} /> Ouvrir la fiche contact
              </button>
            </div>
          ) : (
            <div className="text-sm text-slate-300">Numéro inconnu — nouveau prospect potentiel</div>
          )}

          {history_calls?.length > 0 && (
            <div data-testid="popup-history">
              <div className="text-[10px] font-semibold text-slate-500 uppercase flex items-center gap-1 mb-1"><History size={11} /> Derniers appels de ce numéro</div>
              <div className="space-y-1">
                {history_calls.map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-xs text-slate-400">
                    <span>{fmtDate(h.created_at)}</span>
                    <span className={cn("px-1.5 py-0.5 rounded text-[10px]", RESULT_META[h.result]?.cls)}>{RESULT_META[h.result]?.label || h.result}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activities?.length > 0 && (
            <div data-testid="popup-activities">
              <div className="text-[10px] font-semibold text-slate-500 uppercase flex items-center gap-1 mb-1"><Activity size={11} /> Dernières activités</div>
              <div className="space-y-1">
                {activities.map((a, i) => (
                  <div key={i} className="text-xs text-slate-400 truncate">
                    <span className="text-slate-500">{fmtDate(a.created_at)}</span> · {a.type} — {a.subject || a.result || "—"}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-3 flex gap-2 border-t border-slate-800">
          <button data-testid="popup-answer-btn" onClick={answer}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors">
            <Phone size={16} /> Répondre
          </button>
          <button data-testid="popup-decline-btn" onClick={decline}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-rose-600/90 hover:bg-rose-600 text-white text-sm font-semibold transition-colors">
            <PhoneOff size={16} /> Refuser
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
